// The controls strip above a ranking question: which view its answers are charted in, and -- for
// the two views that stack them -- what the bands are labelled with.

import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Dropdown } from 'primereact/dropdown'

import {
  CHART_LABELS_MAPPING,
  CHART_VIEW_MAPPING,
  DEFAULT_CHART_LABELS
} from '@aytm/app/stats/page/question-views/components/lib/constants'
import {
  getDefaultChartView,
  resolveAvailableViewModesByFormat
} from '@aytm/app/stats/page/question-views/components/controls/lib/view-modes'
import ControlsWithToggle from '@aytm/app/stats/page/question-views/components/controls/ControlsWithToggle'

const TYPE = 'ro'

// A long list of answers is labelled by position rather than by name, which would not fit.
const MAX_NAMED_ANSWERS = 20

const isBanded = viewMode => ['area', 'stacked'].includes(viewMode)

const defaultLabelsOf = total => (MAX_NAMED_ANSWERS < total ? 'positions' : DEFAULT_CHART_LABELS)

// Moving between the two banded views keeps the labels; arriving at one from a view with no bands
// starts them over.
const calcLabelsMode = ({ viewMode, prevViewMode, prevLabelsMode, total }) => {
  if (isBanded(prevViewMode) && isBanded(viewMode)) return prevLabelsMode

  return isBanded(viewMode) ? defaultLabelsOf(total) : DEFAULT_CHART_LABELS
}

const ControlsRO = ({
  viewMode,
  format,
  batches,
  labelsMode,
  total,
  itemsCollapsed,
  onToggleBatches,
  onModelChange
}) => {
  const options = useMemo(() => resolveAvailableViewModesByFormat(CHART_VIEW_MAPPING[TYPE], format), [format])

  const defaultLabelsMode = useMemo(() => defaultLabelsOf(total), [total])

  // a single view is no choice to offer, so the strip stays out of the way
  if (null == options || 1 === options.length) return null

  const onViewModeChange = event =>
    onModelChange({
      viewMode: event.value,
      labelsMode: calcLabelsMode({
        viewMode: event.value,
        prevViewMode: viewMode,
        prevLabelsMode: labelsMode || defaultLabelsMode,
        total
      })
    })

  return (
    <ControlsWithToggle
      type={TYPE}
      itemsLabel="answers"
      batches={batches}
      itemsCollapsed={itemsCollapsed}
      onToggleBatches={onToggleBatches}
    >
      <div className="p-fluid p-col-3">
        <Dropdown
          className="p-dropdown-rounded"
          aria-label="View mode"
          ariaLabel="View mode"
          value={viewMode || getDefaultChartView(options)}
          options={options}
          optionValue="value"
          optionLabel="text"
          optionDisabled="disabled"
          fixedDropdownDir="bottom"
          onChange={onViewModeChange}
        />
      </div>
      {isBanded(viewMode) ? (
        <div className="p-fluid p-col-3">
          <Dropdown
            className="p-dropdown-rounded"
            aria-label="Labels"
            ariaLabel="Labels"
            value={labelsMode || defaultLabelsMode}
            options={CHART_LABELS_MAPPING}
            optionValue="value"
            optionLabel="text"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            onChange={event => onModelChange({ labelsMode: event.value })}
          />
        </div>
      ) : null}
    </ControlsWithToggle>
  )
}

ControlsRO.propTypes = {
  viewMode: PropTypes.string,
  format: PropTypes.object,
  batches: PropTypes.array,
  labelsMode: PropTypes.string,
  total: PropTypes.number,
  itemsCollapsed: PropTypes.bool,
  onToggleBatches: PropTypes.func,
  onModelChange: PropTypes.func
}

ControlsRO.displayName = 'ControlsRO'

export default ControlsRO
